import { useState, useEffect, useCallback, useRef } from "react";
import { Send, Users, Trash2, X, Loader2, CheckCircle, AlertCircle, Clock, MessageSquare, Paperclip, Image as ImageIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import axios from "axios";
import { PullItemsEditor } from "./PullItemsEditor";
import { AssignmentDetail } from "./AssignmentDetail";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function OrderAssignmentSection({ getAuthHeader, employees: employeesProp }) {
  const [assignments, setAssignments] = useState([]);
  const [labels, setLabels] = useState([]);
  const [employeesList, setEmployeesList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [showForm, setShowForm] = useState(false);

  const [selectedEmployee, setSelectedEmployee] = useState("");
  const [selectedLabels, setSelectedLabels] = useState([]);
  const [notes, setNotes] = useState("");
  const getLocalDate = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  };
  const [sinceDate, setSinceDate] = useState(getLocalDate);
  const [untilDate, setUntilDate] = useState(getLocalDate);
  const [previewCount, setPreviewCount] = useState(null);
  const [detail, setDetail] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const employees = (employeesProp && employeesProp.length > 0) ? employeesProp : employeesList;

  // Robust date handler — iOS sometimes fires onInput but not onChange
  const handleDateChange = (setter) => (e) => {
    const val = e.target.value;
    if (val) setter(val);
  };

  // Live preview: fetch item count + match preview when dates change
  useEffect(() => {
    if (!showForm || !sinceDate || !untilDate) return;
    let cancelled = false;
    const fetchPreview = async () => {
      setPreviewLoading(true);
      try {
        const params = new URLSearchParams({ since: sinceDate, until: untilDate });
        const { data } = await axios.get(
          `${API}/orders/preview-count?${params.toString()}`,
          getAuthHeader()
        );
        if (!cancelled) {
          setPreviewCount(data.count);
        }
      } catch {
        if (!cancelled) setPreviewCount(null);
      } finally {
        if (!cancelled) setPreviewLoading(false);
      }
    };
    fetchPreview();
    return () => { cancelled = true; };
  }, [sinceDate, untilDate, showForm, getAuthHeader]);

  const fetchAssignments = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/orders/assignments`, getAuthHeader());
      setAssignments(data.assignments || []);
    } catch (err) { /* ignore */ }
  }, [getAuthHeader]);

  const fetchLabels = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/orders/labels`, getAuthHeader());
      setLabels(data.labels || []);
    } catch (err) { /* ignore */ }
  }, [getAuthHeader]);

  const fetchEmployees = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/employees`, getAuthHeader());
      setEmployeesList(data || []);
    } catch (err) { /* ignore */ }
  }, [getAuthHeader]);

  useEffect(() => {
    setLoading(true);
    Promise.all([fetchAssignments(), fetchLabels(), fetchEmployees()]).finally(() => setLoading(false));
  }, [fetchAssignments, fetchLabels, fetchEmployees]);

  const handleAssign = async () => {
    if (!selectedEmployee) { toast.error("Select an employee"); return; }
    setAssigning(true);
    try {
      const { data } = await axios.post(`${API}/orders/assign`, {
        employee_id: selectedEmployee,
        since: sinceDate,
        until: untilDate,
        label_ids: selectedLabels.length > 0 ? selectedLabels : labels.map((l) => l.id),
        notes,
      }, getAuthHeader());
      toast.success(`Orders assigned to ${data.employee_name}`, {
        description: `${data.item_count} items, ${data.label_count} labels`,
      });
      setShowForm(false);
      setSelectedEmployee("");
      setSelectedLabels([]);
      setNotes("");
      fetchAssignments();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to assign");
    } finally {
      setAssigning(false);
    }
  };

  const handleDelete = async (id, empName) => {
    const ok = window.confirm(`Remove this assignment for ${empName} from the log?`);
    if (!ok) return;
    try {
      await axios.delete(`${API}/orders/assignments/${id}`, getAuthHeader());
      toast.success("Removed from log");
      fetchAssignments();
    } catch (err) {
      toast.error("Failed to remove");
    }
  };

  const toggleLabel = (id) => {
    setSelectedLabels((prev) => prev.includes(id) ? prev.filter((l) => l !== id) : [...prev, id]);
  };

  // Update label note
  const updateLabelNote = async (labelId, note) => {
    try {
      await axios.patch(`${API}/orders/labels/${labelId}`, { note }, getAuthHeader());
      setLabels((prev) => prev.map((l) => l.id === labelId ? { ...l, note } : l));
    } catch {
      toast.error("Failed to save note");
    }
  };

  // Upload attachment image to a label
  const uploadAttachment = async (labelId, file) => {
    try {
      const formData = new FormData();
      formData.append("file", file);
      const { data } = await axios.post(`${API}/orders/labels/${labelId}/attachment`, formData, {
        headers: { ...getAuthHeader().headers, "Content-Type": "multipart/form-data" },
        timeout: 30000,
      });
      setLabels((prev) => prev.map((l) => l.id === labelId ? { ...l, attachment_url: data.url, attachment_name: data.filename } : l));
      toast.success("Image attached");
    } catch {
      toast.error("Failed to upload image");
    }
  };

  // Remove attachment
  const removeAttachment = async (labelId) => {
    try {
      await axios.delete(`${API}/orders/labels/${labelId}/attachment`, getAuthHeader());
      setLabels((prev) => prev.map((l) => l.id === labelId ? { ...l, attachment_url: "", attachment_name: "" } : l));
      toast.success("Attachment removed");
    } catch {
      toast.error("Failed to remove attachment");
    }
  };

  const active = assignments.filter((a) => a.status === "active");
  const history = assignments.filter((a) => a.status !== "active");

  const statusConfig = {
    completed: { icon: CheckCircle, color: "#10B981", bg: "bg-[#10B981]/5", border: "border-[#10B981]/15", label: "Completed" },
    incomplete: { icon: AlertCircle, color: "#F59E0B", bg: "bg-[#F59E0B]/5", border: "border-[#F59E0B]/15", label: "Incomplete" },
    replaced: { icon: AlertCircle, color: "#F59E0B", bg: "bg-[#F59E0B]/5", border: "border-[#F59E0B]/15", label: "Incomplete" },
  };

  const formatDate = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  };

  return (
    <div className="dashboard-card" data-testid="order-assignments-section">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#8B5CF6] to-[#6D28D9] flex items-center justify-center">
            <Send className="w-4 h-4 text-white" />
          </div>
          <h2 className="font-playfair text-xl font-semibold text-[#333]">Order Assignments</h2>
        </div>
        <Button
          size="sm"
          onClick={() => { setShowForm(!showForm); if (!showForm) { fetchLabels(); fetchEmployees(); } }}
          className="bg-[#8B5CF6] hover:bg-[#7C3AED] text-white text-xs"
          data-testid="assign-orders-btn"
        >
          <Send className="w-3.5 h-3.5 mr-1" />
          Assign Orders
        </Button>
      </div>

      {/* Assign Form */}
      {showForm && (
        <div className="mb-4 p-4 rounded-xl bg-[#fafafa] border border-[#eee] space-y-3">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-semibold text-[#333]">New Assignment</h4>
            <button onClick={() => setShowForm(false)} className="text-[#ccc] hover:text-[#888]"><X className="w-4 h-4" /></button>
          </div>
          <div>
            <label className="text-xs text-[#888] mb-1 block font-medium">Employee</label>
            <div className="flex flex-wrap gap-1.5" data-testid="assign-employee-select">
              {(employees || []).map((emp) => (
                <button
                  key={emp.id}
                  type="button"
                  onClick={() => setSelectedEmployee(emp.id)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                    selectedEmployee === emp.id
                      ? "bg-[#8B5CF6]/15 text-[#8B5CF6] border border-[#8B5CF6]/40"
                      : "bg-white text-[#666] border border-[#ddd] hover:bg-[#f5f5f5]"
                  }`}
                  data-testid={`assign-employee-option-${emp.id}`}
                >
                  {emp.name}
                </button>
              ))}
              {(!employees || employees.length === 0) && (
                <span className="text-xs text-[#aaa] italic py-1.5">Loading employees...</span>
              )}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs text-[#888] mb-1 block font-medium">From</label>
              <input type="date" value={sinceDate}
                onChange={handleDateChange(setSinceDate)}
                onInput={handleDateChange(setSinceDate)}
                onBlur={handleDateChange(setSinceDate)}
                className="w-full bg-white border border-[#ddd] rounded-lg px-3 py-2 text-sm text-[#333] focus:outline-none focus:border-[#8B5CF6]"
                data-testid="assign-since-date" />
            </div>
            <div>
              <label className="text-xs text-[#888] mb-1 block font-medium">To</label>
              <input type="date" value={untilDate}
                onChange={handleDateChange(setUntilDate)}
                onInput={handleDateChange(setUntilDate)}
                onBlur={handleDateChange(setUntilDate)}
                className="w-full bg-white border border-[#ddd] rounded-lg px-3 py-2 text-sm text-[#333] focus:outline-none focus:border-[#8B5CF6]"
                data-testid="assign-until-date" />
            </div>
          </div>
          {/* Live item count preview */}
          <div className={`px-3 py-2 rounded-lg text-xs font-medium ${
            previewLoading ? "bg-[#f5f5f5] text-[#aaa]" :
            previewCount === 0 ? "bg-red-50 text-red-500 border border-red-200" :
            previewCount > 0 ? "bg-green-50 text-green-600 border border-green-200" :
            "bg-[#f5f5f5] text-[#aaa]"
          }`} data-testid="preview-item-count">
            {previewLoading ? "Counting orders..." :
             previewCount === 0 ? "0 orders found for this date range — check your dates" :
             previewCount > 0 ? (
               <span>{previewCount} orders found for {sinceDate} to {untilDate}</span>
             ) :
             "Select dates to preview"}
          </div>
          {previewCount > 0 && (
            <PullItemsEditor sinceDate={sinceDate} untilDate={untilDate} labels={labels} getAuthHeader={getAuthHeader} />
          )}
          {labels.length > 0 && (
            <div>
              <label className="text-xs text-[#888] mb-1 block font-medium">
                Labels ({selectedLabels.length === 0 ? "all included" : `${selectedLabels.length} selected`})
              </label>
              <div className="space-y-2 max-h-[280px] overflow-y-auto">
                {labels.map((label) => (
                  <div key={label.id} className="p-2 rounded-lg bg-white border border-[#eee] space-y-1.5">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" checked={selectedLabels.length === 0 || selectedLabels.includes(label.id)}
                        onChange={() => toggleLabel(label.id)} className="rounded border-[#ccc]" />
                      <span className="text-xs text-[#555] truncate flex-1">{label.display_name || label.filename}</span>
                      {label.sku_tag && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#8B5CF6]/10 text-[#8B5CF6] font-mono font-bold">{label.sku_tag}</span>
                      )}
                      {label.platform_guess && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#f0f0f0] text-[#888] capitalize">{label.platform_guess}</span>
                      )}
                    </label>
                    {/* Note + Attachment row */}
                    <div className="flex items-center gap-1.5 ml-6">
                      <MessageSquare className="w-3 h-3 text-[#bbb] flex-shrink-0" />
                      <input
                        type="text"
                        defaultValue={label.note || ""}
                        placeholder="Add note (e.g., 'Blue Nike shoes, shelf B3')"
                        onBlur={(e) => {
                          const val = e.target.value.trim();
                          if (val !== (label.note || "")) updateLabelNote(label.id, val);
                        }}
                        className="flex-1 text-[11px] bg-[#fafafa] border border-[#eee] rounded px-2 py-1 text-[#555] focus:outline-none focus:border-[#8B5CF6]/50"
                        data-testid={`label-note-${label.id}`}
                      />
                      {/* Attachment button */}
                      {label.attachment_url ? (
                        <div className="flex items-center gap-1">
                          <img src={label.attachment_url} alt="" className="w-7 h-7 rounded object-cover border border-[#ddd]" />
                          <button onClick={() => removeAttachment(label.id)} className="text-[#ccc] hover:text-red-400 transition-colors" title="Remove image">
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ) : (
                        <label className="cursor-pointer text-[#bbb] hover:text-[#8B5CF6] transition-colors" title="Attach item photo">
                          <ImageIcon className="w-4 h-4" />
                          <input type="file" accept="image/*" className="hidden"
                            onChange={(e) => { if (e.target.files[0]) uploadAttachment(label.id, e.target.files[0]); }}
                            data-testid={`label-attach-${label.id}`}
                          />
                        </label>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div>
            <label className="text-xs text-[#888] mb-1 block font-medium">Notes (optional)</label>
            <input type="text" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="e.g., Priority items first..."
              className="w-full bg-white border border-[#ddd] rounded-lg px-3 py-2 text-sm text-[#333] focus:outline-none focus:border-[#8B5CF6]" />
          </div>
          <Button onClick={handleAssign} disabled={assigning || !selectedEmployee || previewCount === 0} className="w-full bg-[#8B5CF6] hover:bg-[#7C3AED] text-white" data-testid="confirm-assign-btn">
            {assigning ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
            {assigning ? "Assigning..." : previewCount > 0 ? `Assign ${previewCount} Orders` : "Assign to Employee"}
          </Button>
        </div>
      )}

      {/* Active Assignments */}
      {active.length > 0 && (
        <div className="space-y-2 mb-4">
          <h4 className="text-xs font-semibold text-[#aaa] uppercase tracking-wider">Active</h4>
          {active.map((a) => (
            <div key={a.id} className="flex items-center justify-between p-3 rounded-lg bg-[#8B5CF6]/5 border border-[#8B5CF6]/20" data-testid={`assignment-${a.id}`}>
              <div className="flex items-center gap-3 flex-1 cursor-pointer" onClick={() => setDetail(a)} data-testid={`view-assignment-${a.id}`}>
                <div className="w-8 h-8 rounded-full bg-[#8B5CF6]/10 flex items-center justify-center">
                  <Clock className="w-4 h-4 text-[#8B5CF6]" />
                </div>
                <div>
                  <p className="text-sm text-[#333] font-medium">{a.employee_name}</p>
                  <p className="text-xs text-[#999]">
                    {a.item_count} items, {a.label_count} labels
                    {a.since && ` — ${a.since}`}
                    {a.until && a.until !== a.since && ` to ${a.until}`}
                  </p>
                  <p className="text-[10px] text-[#bbb]">Assigned {formatDate(a.assigned_at)}</p>
                </div>
              </div>
              <button onClick={() => handleDelete(a.id, a.employee_name)} className="text-[#ccc] hover:text-red-500 transition-colors" data-testid={`delete-assignment-${a.id}`}>
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Assignment History Log */}
      {history.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-xs font-semibold text-[#aaa] uppercase tracking-wider">History</h4>
          {history.map((a) => {
            const cfg = statusConfig[a.status] || statusConfig.incomplete;
            const Icon = cfg.icon;
            return (
              <div key={a.id} className={`flex items-center justify-between p-3 rounded-lg ${cfg.bg} border ${cfg.border}`} data-testid={`history-${a.id}`}>
                <div className="flex items-center gap-3 flex-1 cursor-pointer" onClick={() => setDetail(a)} data-testid={`view-history-${a.id}`}>
                  <Icon className="w-4 h-4 flex-shrink-0" style={{ color: cfg.color }} />
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="text-sm text-[#333] font-medium">{a.employee_name}</p>
                      <span className="text-[10px] px-1.5 py-0.5 rounded font-semibold" style={{ color: cfg.color, backgroundColor: `${cfg.color}15` }}>
                        {cfg.label}
                      </span>
                    </div>
                    <p className="text-xs text-[#999]">
                      {a.item_count} items{a.pulled_count != null && ` (${a.pulled_count} pulled)`}, {a.label_count} labels
                      {a.since && ` — ${a.since}`}
                      {a.until && a.until !== a.since && ` to ${a.until}`}
                    </p>
                    <p className="text-[10px] text-[#bbb]">
                      {a.status === "completed"
                        ? `Completed ${formatDate(a.completed_at)}`
                        : `Assigned ${formatDate(a.assigned_at)}`}
                    </p>
                  </div>
                </div>
                <button onClick={() => handleDelete(a.id, a.employee_name)} className="text-[#ccc] hover:text-red-500 transition-colors" data-testid={`delete-history-${a.id}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            );
          })}
        </div>
      )}

      {detail && <AssignmentDetail assignment={detail} getAuthHeader={getAuthHeader} onClose={() => setDetail(null)} />}

      {!loading && assignments.length === 0 && !showForm && (
        <p className="text-sm text-[#aaa] text-center py-2">No assignments yet</p>
      )}
    </div>
  );
}
